import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Table, Tabs, Tag, Checkbox, Button, DatePicker, Input, Space,
  Modal, Tooltip, message, Select, Radio,
} from 'antd'
import {
  SearchOutlined, ReloadOutlined, CheckCircleOutlined,
  ShoppingCartOutlined, InboxOutlined, WarningOutlined,
  RollbackOutlined,
} from '@ant-design/icons'
import { AutoRefreshBadge } from '@/components/AutoRefreshBadge'
import { TagArticoliResi } from '@/components/TagArticoliResi'
import dayjs, { Dayjs } from 'dayjs'
import axiosInstance from '@/lib/axios'
import { usePageTitle } from '@/contexts/PageTitleContext'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Alert, Typography } from 'antd'
import './OrdiniPage.css'

interface OrdineItem {
  id: number
  descrizione: string
  quantita: number
  prezzo_unitario: number | null
  ricevuto: boolean
  data_ricezione_item: string | null
  reso: boolean
  data_resa: string | null
  motivo_resa: string | null
  note_resa: string | null
  sostituisce_item_id: number | null
  offerta_titolo: string | null
  offerta_fornitore: string | null
  offerta_source_url: string | null
}

interface Ordine {
  id: number
  codice: string
  fornitore: string | null
  data_ordine: string | null
  data_consegna_prevista: string | null
  /** Perche' e' stato ordinato senza data: prende il posto della data a schermo. */
  motivo_deroga_data?: string | null
  data_ricezione: string | null
  purchase_request_status_id: number
  work_order_id: number | null
  numero_scheda: string | null
  wo_stato: string | null
  cliente_nome: string | null
  veicolo_targa: string | null
  veicolo_modello: string | null
  items: OrdineItem[]
  items_totali: number
  items_ricevuti: number
  /** Articoli tornati al fornitore e ancora senza sostituto arrivato. */
  articoli_resi?: number
}

const { RangePicker } = DatePicker

const OrdiniPage: React.FC = () => {
  const { setTitle } = usePageTitle()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const consegnaScadutaFiltro = searchParams.get('consegna_scaduta') === 'true'
  const [activeTab, setActiveTab] = useState<'ordinata' | 'ricevuta' | 'incongruenze'>('ordinata')
  const [ordini, setOrdini] = useState<Ordine[]>([])
  const [loading, setLoading] = useState(false)
  const [expandedKeys, setExpandedKeys] = useState<number[]>([])

  // Filtri
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null])
  const [filterCliente, setFilterCliente] = useState('')
  const [filterTarga, setFilterTarga] = useState('')
  const [filterScheda, setFilterScheda] = useState('')

  // Data consegna editing
  const [editingConsegna, setEditingConsegna] = useState<Record<number, Dayjs | null>>({})

  // Items localmente spuntati ma non ancora salvati (in attesa di data)
  // key = itemId, value = data selezionata (null = non ancora selezionata)
  const [pendingItems, setPendingItems] = useState<Record<number, Dayjs | null>>({})

  // Controlla quale DatePicker degli item è esplicitamente aperto (evita auto-apertura al mount)
  const [openPickerId, setOpenPickerId] = useState<number | null>(null)

  // Date per "Tutti ricevuti" per ordine (key = ordineId, assente = nessuna data)
  const [tuttiRicevutiDates, setTuttiRicevutiDates] = useState<Record<number, Dayjs>>({})
  const [lastRefreshed, setLastRefreshed] = useState<number | null>(null)

  // Resa modal
  const [resaModal, setResaModal] = useState<{ open: boolean; item: OrdineItem | null; ordineId: number | null }>({ open: false, item: null, ordineId: null })
  const [resaMotivo, setResaMotivo] = useState<string>('')
  const [resaNote, setResaNote] = useState<string>('')
  // `null` = non ha ancora risposto. Non è un default mascherato: finché resta
  // null il pulsante di conferma è spento, perché nessuna delle due risposte è
  // quella ovvia e sceglierne una al posto dell'utente sarebbe un errore.
  const [resaServeSostituto, setResaServeSostituto] = useState<boolean | null>(null)
  const [resaLoading, setResaLoading] = useState(false)

  useEffect(() => {
    setTitle('Ricezione Ordini')
  }, [setTitle])

  const loadOrdini = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true)
      setPendingItems({})
    }
    try {
      const params: Record<string, string> = { stato: activeTab }
      if (dateRange[0]) params.data_da = dateRange[0].format('YYYY-MM-DD')
      if (dateRange[1]) params.data_a = dateRange[1].format('YYYY-MM-DD')
      if (filterCliente.trim()) params.cliente = filterCliente.trim()
      if (filterTarga.trim()) params.targa = filterTarga.trim()
      if (filterScheda.trim()) params.numero_scheda = filterScheda.trim()
      if (consegnaScadutaFiltro) params.consegna_scaduta = 'true'

      const res = await axiosInstance.get('/purchase-requests/ordini', { params })
      setOrdini(res.data)
      if (!silent) setExpandedKeys([])
      setLastRefreshed(Date.now())
    } catch {
      if (!silent) message.error('Errore nel caricamento degli ordini')
    } finally {
      if (!silent) setLoading(false)
    }
  }, [activeTab, dateRange, filterCliente, filterTarga, filterScheda, consegnaScadutaFiltro])

  useEffect(() => {
    loadOrdini()
  }, [loadOrdini])

  useEffect(() => {
    const id = setInterval(() => loadOrdini(true), 60_000)
    return () => clearInterval(id)
  }, [loadOrdini])

  const handleResa = async () => {
    if (!resaModal.item || !resaModal.ordineId || !resaMotivo) return
    setResaLoading(true)
    try {
      await axiosInstance.post(
        `/purchase-requests/${resaModal.ordineId}/items/${resaModal.item.id}/resa`,
        {
          motivo_resa: resaMotivo,
          note_resa: resaNote || null,
          // Viaggia solo dove ha senso: per gli altri due motivi rimedia lo
          // stesso fornitore e la domanda non è stata nemmeno mostrata.
          serve_sostituto: resaMotivo === 'ACQUISTO_ERRATO' ? resaServeSostituto : null,
        },
      )
      message.success(
        resaMotivo === 'ACQUISTO_ERRATO' && resaServeSostituto
          ? 'Reso registrato. Trovi la nuova offerta in bozza fra le RDO.'
          : 'Reso registrato'
      )
      setResaModal({ open: false, item: null, ordineId: null })
      setResaMotivo('')
      setResaNote('')
      setResaServeSostituto(null)
      await loadOrdini()
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Errore nella registrazione del reso')
    } finally {
      setResaLoading(false)
    }
  }

  const handleItemRicevuto = async (ordineId: number, itemId: number, checked: boolean, dataRicezione?: Dayjs) => {
    try {
      const isoDate = dataRicezione ? dataRicezione.toISOString() : null
      await axiosInstance.patch(`/purchase-requests/items/${itemId}/ricevuto`, {
        ricevuto: checked,
        data_ricezione_item: isoDate,
      })
      setOrdini(prev => prev.map(o => {
        if (o.id !== ordineId) return o
        const updatedItems = o.items.map(it =>
          it.id === itemId
            ? { ...it, ricevuto: checked, data_ricezione_item: isoDate }
            : it
        )
        const ricevuti = updatedItems.filter(it => it.ricevuto).length
        return { ...o, items: updatedItems, items_ricevuti: ricevuti }
      }))

      // Se tutti gli items sono ora ricevuti, proponi avanzamento stato
      if (checked) {
        const ordine = ordini.find(o => o.id === ordineId)
        if (ordine) {
          const updatedItems = ordine.items.map(it => it.id === itemId ? { ...it, ricevuto: true } : it)
          if (updatedItems.every(it => it.ricevuto)) {
            confermaTuttiRicevuti(ordine)
          }
        }
      }
    } catch {
      message.error('Errore nel salvataggio')
    }
  }

  const handleTuttiRicevuti = async (ordine: Ordine, data: Dayjs) => {
    try {
      const isoDate = data.toISOString()
      const itemsDaMarcare = ordine.items.filter(it => !it.ricevuto)
      await Promise.all(
        itemsDaMarcare.map(it =>
          axiosInstance.patch(`/purchase-requests/items/${it.id}/ricevuto`, {
            ricevuto: true,
            data_ricezione_item: isoDate,
          })
        )
      )
      setOrdini(prev => prev.map(o => {
        if (o.id !== ordine.id) return o
        const updatedItems = o.items.map(it => ({
          ...it,
          ricevuto: true,
          // Preserva la data esistente; applica solo a items non ancora ricevuti
          data_ricezione_item: it.ricevuto ? it.data_ricezione_item : isoDate,
        }))
        return { ...o, items: updatedItems, items_ricevuti: updatedItems.length }
      }))
      confermaTuttiRicevuti(ordine)
    } catch {
      message.error('Errore nel salvataggio')
    }
  }

  const handleAnnullaTuttiRicevuti = async (ordine: Ordine) => {
    try {
      await Promise.all(
        ordine.items.map(it =>
          axiosInstance.patch(`/purchase-requests/items/${it.id}/ricevuto`, {
            ricevuto: false,
            data_ricezione_item: null,
          })
        )
      )
      setOrdini(prev => prev.map(o => {
        if (o.id !== ordine.id) return o
        const updatedItems = o.items.map(it => ({ ...it, ricevuto: false, data_ricezione_item: null }))
        return { ...o, items: updatedItems, items_ricevuti: 0 }
      }))
      setTuttiRicevutiDates(prev => {
        const next = { ...prev }
        delete next[ordine.id]
        return next
      })
      setPendingItems({})
      message.success(`Ricezione ordine ${ordine.codice} annullata`)
    } catch {
      message.error('Errore nell\'annullamento')
    }
  }

  const confermaTuttiRicevuti = (ordine: Ordine) => {
    Modal.confirm({
      title: 'Tutti gli articoli ricevuti',
      content: `Tutti gli articoli dell'ordine ${ordine.codice} sono stati marcati come ricevuti. Vuoi avanzare l'ordine allo stato "Ricevuta"?`,
      okText: 'Sì, segna come Ricevuta',
      cancelText: 'No, lascia in Ordinata',
      onOk: async () => {
        try {
          await axiosInstance.post(`/purchase-requests/${ordine.id}/transition/6`, {})
          message.success(`Ordine ${ordine.codice} segnato come Ricevuta`)
          loadOrdini()
        } catch (err: any) {
          // Il messaggio deve dire due cose insieme, perche' sono vere insieme:
          // gli articoli SONO registrati (la PATCH e' andata a buon fine e
          // "Ricezione salvata" era corretto), ma l'ordine NON e' chiuso.
          // Senza la prima meta' l'utente teme di aver perso il lavoro fatto;
          // senza la seconda crede di aver finito e non torna piu' sull'ordine.
          const dettaglio = err?.response?.data?.detail
          message.error({
            content:
              `Articoli registrati, ma l'ordine ${ordine.codice} resta in "Ordinata": `
              + `${dettaglio || 'la chiusura non e\u2019 riuscita'}. `
              + 'Chi ha i permessi puo\u2019 chiuderlo dalla stessa schermata.',
            duration: 8,
          })
        }
      },
    })
  }

  const handleUpdateConsegna = async (ordineId: number) => {
    const val = editingConsegna[ordineId]
    try {
      await axiosInstance.patch(`/purchase-requests/${ordineId}/data-consegna`, {
        data_consegna_prevista: val ? val.format('YYYY-MM-DD') : null,
      })
      setOrdini(prev => prev.map(o =>
        o.id === ordineId
          ? { ...o, data_consegna_prevista: val ? val.format('YYYY-MM-DD') : null }
          : o
      ))
      setEditingConsegna(prev => { const n = { ...prev }; delete n[ordineId]; return n })
      message.success('Data consegna aggiornata')
    } catch {
      message.error('Errore nell\'aggiornamento')
    }
  }

  const [savingOrdineId, setSavingOrdineId] = useState<number | null>(null)

  const removePending = (itemId: number) => {
    setPendingItems(prev => { const n = { ...prev }; delete n[itemId]; return n })
  }

  const handleSaveRicezione = async (ordine: Ordine) => {
    const itemsPending = ordine.items.filter(it => pendingItems[it.id] !== undefined)
    const missingDate = itemsPending.some(it => pendingItems[it.id] === null)
    if (missingDate) {
      message.warning('Imposta la data di ricezione per tutti gli articoli selezionati')
      return
    }
    setSavingOrdineId(ordine.id)
    try {
      await Promise.all(
        itemsPending.map(it =>
          axiosInstance.patch(`/purchase-requests/items/${it.id}/ricevuto`, {
            ricevuto: true,
            data_ricezione_item: pendingItems[it.id]!.toISOString(),
          })
        )
      )
      // Aggiorna stato locale
      setOrdini(prev => prev.map(o => {
        if (o.id !== ordine.id) return o
        const updatedItems = o.items.map(it => {
          if (pendingItems[it.id] !== undefined) {
            return { ...it, ricevuto: true, data_ricezione_item: pendingItems[it.id]!.toISOString() }
          }
          return it
        })
        return { ...o, items: updatedItems, items_ricevuti: updatedItems.filter(it => it.ricevuto).length }
      }))
      // Rimuove dal pending tutti gli item salvati
      setPendingItems(prev => {
        const next = { ...prev }
        itemsPending.forEach(it => delete next[it.id])
        return next
      })
      setOpenPickerId(null)
      message.success('Ricezione salvata')
      // Se tutti ricevuti → proponi avanzamento stato
      const updatedOrdine = ordini.find(o => o.id === ordine.id)
      if (updatedOrdine) {
        const allNowReceived = updatedOrdine.items.every(
          it => pendingItems[it.id] !== undefined || it.ricevuto
        )
        if (allNowReceived) confermaTuttiRicevuti(ordine)
      }
    } catch {
      message.error('Errore nel salvataggio')
    } finally {
      setSavingOrdineId(null)
    }
  }

  const expandedRowRender = (ordine: Ordine) => {
    // Blocca altri checkbox se c'è almeno un item in pending senza data impostata
    const hasPendingWithoutDate = ordine.items.some(
      it => pendingItems[it.id] !== undefined && pendingItems[it.id] === null
    )

    return (
    <Table
      dataSource={ordine.items}
      rowKey="id"
      size="small"
      pagination={false}
      style={{ marginLeft: 16 }}
      columns={[
        {
          title: '',
          width: 40,
          render: (_, item) => {
            const isPending = pendingItems[item.id] !== undefined
            const blockedByOther = hasPendingWithoutDate && !item.ricevuto && !isPending
            return (
              <Tooltip title={blockedByOther ? 'Imposta prima la data dell\'articolo già selezionato' : (item.reso && item.ricevuto) ? 'Articolo reso — non modificabile' : undefined}>
                <Checkbox
                  checked={item.ricevuto || isPending}
                  disabled={activeTab === 'ricevuta' || blockedByOther || (item.reso && item.ricevuto)}
                  onChange={e => {
                    if (e.target.checked) {
                      setPendingItems(prev => ({ ...prev, [item.id]: null }))
                    } else {
                      // Deselezionando un item → pulisce la data "tutti ricevuti"
                      setTuttiRicevutiDates(prev => {
                        const next = { ...prev }
                        delete next[ordine.id]
                        return next
                      })
                      if (isPending) {
                        removePending(item.id)
                        setOpenPickerId(null)
                      } else {
                        handleItemRicevuto(ordine.id, item.id, false)
                      }
                    }
                  }}
                />
              </Tooltip>
            )
          },
        },
        {
          title: 'Descrizione',
          dataIndex: 'descrizione',
          render: (desc, item) => {
            const MOTIVO_LABEL: Record<string, string> = {
              DANNEGGIATO: 'Articolo danneggiato',
              ACQUISTO_ERRATO: 'Acquisto errato',
              ARTICOLO_ERRATO_FORNITORE: 'Articolo errato (fornitore)',
            }
            return (
              <div>
                <div style={{ fontWeight: 500 }}>{desc || <span style={{ color: 'var(--color-text-tertiary)', fontStyle: 'italic' }}>Da compilare</span>}</div>
                {item.offerta_titolo && item.offerta_titolo !== desc && (
                  <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>
                    Offerta: {item.offerta_titolo}
                  </div>
                )}
                {item.reso && item.motivo_resa && (
                  <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', marginTop: 2 }}>
                    Reso: {MOTIVO_LABEL[item.motivo_resa] ?? item.motivo_resa}
                    {item.note_resa && ` — ${item.note_resa}`}
                  </div>
                )}
                {item.sostituisce_item_id != null && !item.reso && (
                  <div style={{ fontSize: '11px', color: '#1677ff', marginTop: 2 }}>
                    Nuovo articolo (sostituzione)
                  </div>
                )}
              </div>
            )
          },
        },
        {
          title: 'Qtà',
          dataIndex: 'quantita',
          width: 60,
          align: 'center',
        },
        {
          title: 'Prezzo',
          dataIndex: 'prezzo_unitario',
          width: 90,
          align: 'right',
          render: v => v != null ? `€ ${Number(v).toFixed(2)}` : '—',
        },
        {
          title: 'Fornitore offerta',
          dataIndex: 'offerta_fornitore',
          width: 150,
          render: (v, item) => v
            ? item.offerta_source_url
              ? <a href={item.offerta_source_url} target="_blank" rel="noreferrer">{v}</a>
              : v
            : '—',
        },
        {
          title: 'Ricevuto il',
          dataIndex: 'data_ricezione_item',
          width: 180,
          render: (v, item) => {
            const isPending = pendingItems[item.id] !== undefined
            const pendingDate = pendingItems[item.id] ?? null

            if (!item.ricevuto && !isPending) {
              return <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>—</span>
            }

            const currentValue = isPending
              ? (pendingDate ?? undefined)
              : (v ? dayjs(v) : undefined)

            return (
              <Space size={4}>
                <DatePicker
                  size="small"
                  format="DD/MM/YYYY"
                  value={currentValue}
                  status={isPending && !pendingDate ? 'error' : undefined}
                  placeholder="Data ricezione"
                  disabled={!isPending && activeTab === 'ricevuta'}
                  allowClear
                  // Controlled open: impedisce auto-apertura al mount del componente
                  open={isPending ? openPickerId === item.id : undefined}
                  onOpenChange={open => {
                    if (isPending) setOpenPickerId(open ? item.id : null)
                  }}
                  onChange={date => {
                    if (!date) {
                      if (isPending) {
                        setPendingItems(prev => ({ ...prev, [item.id]: null }))
                      } else {
                        handleItemRicevuto(ordine.id, item.id, false)
                      }
                      return
                    }
                    if (isPending) {
                      setPendingItems(prev => ({ ...prev, [item.id]: date }))
                      setOpenPickerId(null)
                    } else {
                      handleItemRicevuto(ordine.id, item.id, true, date)
                    }
                  }}
                  style={{ width: 120 }}
                />
              </Space>
            )
          },
        },
        {
          title: '',
          width: 120,
          render: (_, item) => {
            const isPending = pendingItems[item.id] !== undefined
            if (isPending) return <Tag color="warning" style={{ fontSize: '11px' }}>In attesa</Tag>
            // Un articolo reso non finisce MAI nel ramo verde qui sotto: i due
            // rami che seguono coprono ogni combinazione di `reso`.
            //
            // Il campo che distingue i casi e' il motivo, non la presenza di
            // una riga di sostituzione: dal 13/09/2026 nessun reso crea
            // articoli dentro l'ordine (l'acquisto errato apre una bozza a
            // parte, che qui non compare).
            if (item.reso && !item.ricevuto) return <Tag color="volcano" style={{ fontSize: '11px' }}>Reso – in attesa</Tag>
            if (item.reso) {
              // Tornato "ricevuto" con un reso addosso: o e' l'acquisto errato
              // (il pezzo e' qui ma non serviva, nessuno lo sostituira'), oppure
              // e' un danneggiato la cui sostituzione e' arrivata — e allora la
              // pratica e' chiusa bene. MANUALE §5, "Stato badge articolo".
              return item.motivo_resa === 'ACQUISTO_ERRATO'
                ? <Tag color="red" style={{ fontSize: '11px' }}>Reso</Tag>
                : <Tag color="success" icon={<CheckCircleOutlined />} style={{ fontSize: '11px' }}>Ricevuto</Tag>
            }
            return item.ricevuto
              ? <Tag color="success" icon={<CheckCircleOutlined />} style={{ fontSize: '11px' }}>Ricevuto</Tag>
              : <Tag color="default" style={{ fontSize: '11px' }}>Attesa</Tag>
          },
        },
        {
          title: '',
          width: 110,
          render: (_, item) => {
            const MOTIVO_SHORT: Record<string, string> = {
              DANNEGGIATO: 'Danneggiato',
              ACQUISTO_ERRATO: 'Acq. errato',
              ARTICOLO_ERRATO_FORNITORE: 'Art. errato (forn.)',
            }
            if (item.reso) {
              // Stessa regola del badge, per lo stesso motivo: il reso e' chiuso
              // quando il pezzo e' (ri)arrivato e non era un acquisto errato.
              // L'acquisto errato non si "risolve" mai — il motivo resta scritto.
              const isResolved = item.ricevuto && item.motivo_resa !== 'ACQUISTO_ERRATO'
              if (isResolved) return null
              return (
                <span style={{ fontSize: '11px', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                  {item.motivo_resa ? MOTIVO_SHORT[item.motivo_resa] ?? item.motivo_resa : '—'}
                </span>
              )
            }
            if (item.ricevuto && !item.reso) {
              return (
                <Button
                  size="small"
                  icon={<RollbackOutlined />}
                  onClick={() => setResaModal({ open: true, item, ordineId: ordine.id })}
                  style={{ fontSize: '11px' }}
                >
                  Reso
                </Button>
              )
            }
            return null
          },
        },
      ]}
      footer={() => {
        const hasPending = ordine.items.some(it => pendingItems[it.id] !== undefined)
        if (!hasPending || activeTab === 'ricevuta') return null
        const hasUndated = ordine.items.some(
          it => pendingItems[it.id] !== undefined && pendingItems[it.id] === null
        )
        return (
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '4px 0' }}>
            <Button
              type="primary"
              size="small"
              loading={savingOrdineId === ordine.id}
              disabled={hasUndated}
              onClick={() => handleSaveRicezione(ordine)}
            >
              Salva ricezione
            </Button>
          </div>
        )
      }}
    />
    )
  }

  const columns = [
    {
      // In questa pagina lo stato dell'ordine e' la scheda in cui ci si trova,
      // non una colonna: il contrassegno dei resi sta quindi accanto al codice,
      // che e' l'identita' dell'ordine a schermo.
      title: 'Codice',
      dataIndex: 'codice',
      width: 160,
      render: (v: string, r: Ordine) => (
        <Space size={4} wrap>
          <strong>{v}</strong>
          <TagArticoliResi quanti={r.articoli_resi} compatto />
        </Space>
      ),
    },
    {
      title: 'Cliente',
      dataIndex: 'cliente_nome',
      render: (v: string | null) => v || '—',
    },
    {
      title: 'Veicolo',
      width: 160,
      render: (_: any, r: Ordine) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.veicolo_targa || '—'}</div>
          {r.veicolo_modello && <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>{r.veicolo_modello}</div>}
        </div>
      ),
    },
    {
      title: 'Scheda',
      key: 'scheda',
      width: 110,
      render: (_: any, record: Ordine) => (
        <div style={{ lineHeight: 1.3 }}>
          <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 600 }}>
            {record.numero_scheda || '—'}
          </div>
          {record.wo_stato && (
            <Tag style={{ fontSize: '10px', marginTop: 2, padding: '0 4px' }}>
              {record.wo_stato}
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Fornitore',
      dataIndex: 'fornitore',
      render: (v: string | null) => v || '—',
    },
    {
      title: 'Data Ordine',
      dataIndex: 'data_ordine',
      width: 100,
      render: (v: string | null) => v ? dayjs(v).format('DD/MM/YY') : '—',
    },
    {
      title: 'Consegna Prevista',
      width: 170,
      render: (_: any, r: Ordine) => {
        // Senza data, al posto del trattino si legge perche': la deroga e' la
        // risposta alla domanda che quella colonna pone.
        const deroga = !r.data_consegna_prevista && r.motivo_deroga_data
          ? (
            <Tooltip title={`Ordinata senza data di consegna: ${r.motivo_deroga_data}`}>
              <Tag color="warning" style={{ fontSize: '11px', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                deroga: {r.motivo_deroga_data}
              </Tag>
            </Tooltip>
          )
          : null
        if (activeTab === 'ricevuta') {
          if (r.data_consegna_prevista) return dayjs(r.data_consegna_prevista).format('DD/MM/YY')
          return deroga ?? '—'
        }
        const editing = r.id in editingConsegna
        const currentVal = editing
          ? editingConsegna[r.id]
          : r.data_consegna_prevista ? dayjs(r.data_consegna_prevista) : null
        return (
          <Space size={4}>
            {!currentVal && deroga}
            <DatePicker
              size="small"
              format="DD/MM/YY"
              value={currentVal}
              style={{ width: 100 }}
              onChange={val => setEditingConsegna(prev => ({ ...prev, [r.id]: val }))}
            />
            {editing && (
              <Button size="small" type="primary" onClick={() => handleUpdateConsegna(r.id)}>
                ✓
              </Button>
            )}
          </Space>
        )
      },
    },
    {
      title: 'Articoli',
      width: 90,
      align: 'center' as const,
      render: (_: any, r: Ordine) => {
        const all = r.items_ricevuti === r.items_totali && r.items_totali > 0
        return (
          <Tag color={all ? 'success' : r.items_ricevuti > 0 ? 'warning' : 'default'}>
            {r.items_ricevuti}/{r.items_totali}
          </Tag>
        )
      },
    },
    ...(activeTab !== 'ricevuta'
      ? [{
          title: 'Ricevuti il',
          width: 130,
          render: (_: any, r: Ordine) => {
            const date = tuttiRicevutiDates[r.id] ?? undefined
            return (
              <DatePicker
                size="small"
                format="DD/MM/YY"
                value={date}
                placeholder="Ricevuti il"
                style={{ width: 110 }}
                allowClear
                onChange={val => {
                  setTuttiRicevutiDates(prev => {
                    const next = { ...prev }
                    if (!val) delete next[r.id]
                    else next[r.id] = val
                    return next
                  })
                  if (val) {
                    handleTuttiRicevuti(r, val)
                  } else {
                    handleAnnullaTuttiRicevuti(r)
                  }
                }}
              />
            )
          },
        }]
      : [{
          title: 'Ricevuti il',
          dataIndex: 'data_ricezione',
          width: 100,
          render: (v: string | null) => v ? dayjs(v).format('DD/MM/YY') : '—',
        }]
    ),
  ]

  const filterBar = (
    <Space wrap style={{ marginBottom: 12 }} align="center">
      <RangePicker
        size="small"
        format="DD/MM/YY"
        placeholder={['Data da', 'Data a']}
        value={dateRange}
        onChange={vals => setDateRange(vals ? [vals[0], vals[1]] : [null, null])}
      />
      <Input
        size="small"
        prefix={<SearchOutlined />}
        placeholder="Cliente"
        value={filterCliente}
        onChange={e => setFilterCliente(e.target.value)}
        style={{ width: 140 }}
        allowClear
      />
      <Input
        size="small"
        prefix={<SearchOutlined />}
        placeholder="Targa"
        value={filterTarga}
        onChange={e => setFilterTarga(e.target.value)}
        style={{ width: 100 }}
        allowClear
      />
      <Input
        size="small"
        prefix={<SearchOutlined />}
        placeholder="N° Scheda"
        value={filterScheda}
        onChange={e => setFilterScheda(e.target.value)}
        style={{ width: 120 }}
        allowClear
      />
      <Button size="small" icon={<ReloadOutlined />} onClick={() => loadOrdini()}>
        Aggiorna
      </Button>
      <AutoRefreshBadge updatedAt={lastRefreshed} />
    </Space>
  )

  return (
    <div style={{ padding: '16px' }}>
      {consegnaScadutaFiltro && (
        <Alert
          type="warning"
          style={{ marginBottom: 12 }}
          message={
            <span>
              Filtro attivo: <Typography.Text strong>consegna scaduta</Typography.Text>
              {' — '}solo ordini con data consegna prevista già trascorsa
            </span>
          }
          action={<Button size="small" onClick={() => navigate('/ordini')}>Rimuovi filtro</Button>}
          showIcon
        />
      )}
      <Tabs
        activeKey={activeTab}
        onChange={key => setActiveTab(key as 'ordinata' | 'ricevuta' | 'incongruenze')}
        items={[
          {
            key: 'ordinata',
            label: (
              <span>
                <ShoppingCartOutlined style={{ marginRight: 6 }} />
                In Attesa di Ricezione
                {activeTab === 'ordinata' && ordini.length > 0 && (
                  <Tag color="processing" style={{ marginLeft: 8, fontSize: '11px' }}>
                    {ordini.length}
                  </Tag>
                )}
              </span>
            ),
          },
          {
            key: 'ricevuta',
            label: (
              <span>
                <InboxOutlined style={{ marginRight: 6 }} />
                Storico Ricevuti
              </span>
            ),
          },
          {
            key: 'incongruenze',
            label: (
              <span style={{ color: activeTab === 'incongruenze' ? undefined : '#faad14' }}>
                <WarningOutlined style={{ marginRight: 6 }} />
                Incongruenze
                {activeTab === 'incongruenze' && ordini.length > 0 && (
                  <Tag color="warning" style={{ marginLeft: 8, fontSize: '11px' }}>
                    {ordini.length}
                  </Tag>
                )}
              </span>
            ),
          },
        ]}
      />

      {filterBar}

      <Table
        dataSource={activeTab === 'ordinata'
          ? [...ordini].sort((a, b) => {
              if (!a.data_consegna_prevista && !b.data_consegna_prevista) return 0
              if (!a.data_consegna_prevista) return 1
              if (!b.data_consegna_prevista) return -1
              return dayjs(a.data_consegna_prevista).valueOf() - dayjs(b.data_consegna_prevista).valueOf()
            })
          : ordini
        }
        rowKey="id"
        columns={columns}
        loading={loading}
        size="small"
        rowClassName={(record: any) => {
          if (activeTab !== 'ordinata' || !record.data_consegna_prevista) return ''
          const diff = dayjs(record.data_consegna_prevista).startOf('day').diff(dayjs().startOf('day'), 'day')
          if (diff < 0) return 'row-consegna-scaduta'
          if (diff === 0) return 'row-consegna-oggi'
          if (diff === 1) return 'row-consegna-domani'
          if (diff === 2) return 'row-consegna-dopodomani'
          return ''
        }}
        expandable={{
          expandedRowKeys: expandedKeys,
          onExpand: (expanded, record) => {
            setExpandedKeys(expanded
              ? [...expandedKeys, record.id]
              : expandedKeys.filter(k => k !== record.id)
            )
          },
          showExpandColumn: false,
          expandedRowRender,
        }}
        onRow={(record) => ({
          style: { cursor: 'pointer' },
          onClick: (e) => {
            const t = e.target as HTMLElement
            if (t.closest('.ant-picker') || t.closest('.ant-btn') || t.closest('button') ||
                t.closest('input') || t.closest('a') || t.closest('.ant-checkbox')) return
            setExpandedKeys(prev =>
              prev.includes(record.id) ? prev.filter(k => k !== record.id) : [...prev, record.id]
            )
          },
        })}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        locale={{ emptyText: activeTab === 'ordinata' ? 'Nessun ordine in attesa' : activeTab === 'incongruenze' ? 'Nessuna incongruenza trovata' : 'Nessun ordine ricevuto' }}
      />

      {/* Modal: Registra Reso */}
      <Modal
        title={<span><RollbackOutlined style={{ marginRight: 8 }} />Registra reso al fornitore</span>}
        open={resaModal.open}
        onCancel={() => {
          setResaModal({ open: false, item: null, ordineId: null })
          setResaMotivo(''); setResaNote(''); setResaServeSostituto(null)
        }}
        onOk={handleResa}
        okText="Conferma reso"
        okButtonProps={{
          disabled: !resaMotivo
            || (resaMotivo === 'ACQUISTO_ERRATO' && resaServeSostituto === null),
          loading: resaLoading,
          danger: true,
        }}
        cancelText="Annulla"
      >
        {resaModal.item && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 500, marginBottom: 12 }}>{resaModal.item.descrizione}</div>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Motivo del reso *</div>
            <Select
              style={{ width: '100%', marginBottom: 16 }}
              placeholder="Seleziona il motivo"
              value={resaMotivo || undefined}
              onChange={v => { setResaMotivo(v); setResaServeSostituto(null) }}
              options={[
                { value: 'DANNEGGIATO', label: 'Articolo danneggiato' },
                { value: 'ACQUISTO_ERRATO', label: 'Acquisto errato (articolo non necessario)' },
                { value: 'ARTICOLO_ERRATO_FORNITORE', label: 'Articolo errato inviato dal fornitore' },
              ]}
            />
            {/* Acquisto errato: l'errore è nostro, il fornitore ha consegnato
                quello che doveva. Quello che torna indietro è il bisogno, non
                l'ordine — e quindi la domanda che conta è un'altra. */}
            {resaMotivo === 'ACQUISTO_ERRATO' && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8, fontWeight: 500 }}>
                  Serve comunque un ricambio al suo posto? *
                </div>
                <Radio.Group
                  value={resaServeSostituto}
                  onChange={e => setResaServeSostituto(e.target.value)}
                  style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
                >
                  <Radio value={true}>Sì, ne ordino un altro</Radio>
                  <Radio value={false}>No, chiudo lì</Radio>
                </Radio.Group>
              </div>
            )}
            {resaMotivo === 'ACQUISTO_ERRATO' && resaServeSostituto === true && (
              <div style={{ marginBottom: 12, padding: '8px 12px', background: 'var(--color-bg-warning, #fffbe6)', borderRadius: 6, fontSize: '12px' }}>
                L'articolo resta segnato come <strong>reso</strong> ed esce dai totali.
                Questo ordine <strong>non cambia stato</strong>: gli altri suoi articoli
                restano come sono. Nasce una <strong>nuova RDO in bozza</strong>, senza
                fornitore, con un articolo da compilare.
              </div>
            )}
            {resaMotivo === 'ACQUISTO_ERRATO' && resaServeSostituto === false && (
              <div style={{ marginBottom: 12, padding: '8px 12px', background: 'var(--color-bg-warning, #fffbe6)', borderRadius: 6, fontSize: '12px' }}>
                L'articolo resta segnato come <strong>reso</strong> ed esce dai totali.
                Questo ordine <strong>non cambia stato</strong> e <strong>non viene creata
                nessuna nuova RDO</strong>.
              </div>
            )}
            {(resaMotivo === 'DANNEGGIATO' || resaMotivo === 'ARTICOLO_ERRATO_FORNITORE') && (
              <div style={{ marginBottom: 12, padding: '8px 12px', background: 'var(--color-bg-warning, #fffbe6)', borderRadius: 6, fontSize: '12px' }}>
                L'articolo tornerà in <strong>attesa di ri-consegna</strong> dal fornitore.
              </div>
            )}
            <div style={{ marginBottom: 4, fontWeight: 500 }}>Note (opzionale)</div>
            <Input.TextArea
              rows={2}
              placeholder="Es. imballaggio rotto, numero seriale errato..."
              value={resaNote}
              onChange={e => setResaNote(e.target.value)}
            />
          </div>
        )}
      </Modal>

    </div>
  )
}

export default OrdiniPage
